<script setup lang="ts">
import { ref, computed, toRef, watch } from "vue";
import { useTranslation } from "i18next-vue";
import type { FieldObservation } from "@/types/field-observations.types";
import {
  useGetFieldObservations,
  useGetFieldParts,
} from "@/stores/queries/field-observations.query";
import { useGetDiagnostic } from "@/stores/queries/diagnostics.query";
import FieldObservationItem from "./FieldObservationItem.vue";
import FieldObservationEditor from "./FieldObservationEditor.vue";
import CameraOverlay from "./CameraOverlay.vue";
import VoiceRecordingOverlay from "./VoiceRecordingOverlay.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import PartsTreeEditor from "./PartsTreeEditor.vue";
import { usePwaController } from "@/composable/usePWA";

const props = defineProps<{
  diagnosticId: number;
}>();

const { t } = useTranslation();
const diagnosticId = toRef(props, "diagnosticId");
const { isMobileOrTablet } = usePwaController();

// Queries
const { data: observations, isLoading } = useGetFieldObservations(diagnosticId);
const { data: parts } = useGetFieldParts(diagnosticId);

// Diagnostic data for AI banners
const { data: diagnostic } = useGetDiagnostic(diagnosticId);
const aiBannerDismissed = ref(false);

const showAiExtractingBanner = computed(
  () =>
    !aiBannerDismissed.value && (diagnostic.value?.isExtractingData ?? false),
);

const showAiSuggestionsBanner = computed(
  () =>
    !aiBannerDismissed.value &&
    !diagnostic.value?.isExtractingData &&
    (diagnostic.value?.partsTree?.parts?.length ?? 0) > 0,
);

const dismissAiBanner = () => {
  aiBannerDismissed.value = true;
};

const openPartsFromBanner = () => {
  aiBannerDismissed.value = true;
  showPartsDrawer.value = true;
};

// Sort
type SortKey = "date" | "type" | "part";
const sortKey = ref<SortKey>("date");

// Expanded sections for type grouping
const expandedTypes = ref<Set<string>>(
  new Set(["DISORDER", "OVERVIEW", "COMMENT"]),
);

const toggleTypeSection = (type: string) => {
  if (expandedTypes.value.has(type)) {
    expandedTypes.value.delete(type);
  } else {
    expandedTypes.value.add(type);
  }
  // Force reactivity
  expandedTypes.value = new Set(expandedTypes.value);
};

// Type configurations
const typeConfigs = {
  DISORDER: {
    icon: "warning",
    color: "#ef4444",
    labelKey: "dashboard:fieldObservations.type.DISORDER",
  },
  OVERVIEW: {
    icon: "camera",
    color: "#3b82f6",
    labelKey: "dashboard:fieldObservations.type.OVERVIEW",
  },
  COMMENT: {
    icon: "chat-text",
    color: "#8b5cf6",
    labelKey: "dashboard:fieldObservations.type.COMMENT",
  },
};

// Parts drawer
const showPartsDrawer = ref(false);

const sortedObservations = computed(() => {
  if (!observations.value) return [];
  const obs = [...observations.value];

  switch (sortKey.value) {
    case "type":
      return obs.sort((a, b) => a.type.localeCompare(b.type));
    case "part":
      return obs.sort((a, b) => {
        const partA = a.part?.name || "";
        const partB = b.part?.name || "";
        return partA.localeCompare(partB);
      });
    case "date":
    default:
      return obs.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
  }
});

// Grouped observations by type
const observationsByType = computed(() => {
  const empty: Record<string, FieldObservation[]> = {
    DISORDER: [],
    OVERVIEW: [],
    COMMENT: [],
  };
  if (!observations.value) return empty;
  return {
    DISORDER: observations.value.filter((o) => o.type === "DISORDER"),
    OVERVIEW: observations.value.filter((o) => o.type === "OVERVIEW"),
    COMMENT: observations.value.filter((o) => o.type === "COMMENT"),
  };
});

// Expanded sections for part grouping
const expandedParts = ref<Set<string | number>>(new Set());

// Initialize expanded parts when parts load
const initExpandedParts = () => {
  const allPartIds = new Set<string | number>(["no-part"]);
  if (parts.value) {
    parts.value.forEach((p) => allPartIds.add(p.id));
  }
  expandedParts.value = allPartIds;
};

// Watch parts to initialize expanded state
watch(
  () => parts.value,
  () => {
    if (expandedParts.value.size === 0) {
      initExpandedParts();
    }
  },
  { immediate: true },
);

const togglePartSection = (partId: string | number) => {
  if (expandedParts.value.has(partId)) {
    expandedParts.value.delete(partId);
  } else {
    expandedParts.value.add(partId);
  }
  expandedParts.value = new Set(expandedParts.value);
};

// Grouped observations by part
const observationsByPart = computed(() => {
  const result: Record<string | number, FieldObservation[]> = {
    "no-part": [],
  };
  if (!observations.value) return result;

  // Initialize all part keys
  if (parts.value) {
    parts.value.forEach((p) => {
      result[p.id] = [];
    });
  }

  // Group observations
  observations.value.forEach((obs) => {
    if (obs.partId && result[obs.partId] !== undefined) {
      result[obs.partId].push(obs);
    } else {
      result["no-part"].push(obs);
    }
  });

  return result;
});

// List of parts to display (with observations count > 0 or all parts)
const partsToDisplay = computed(() => {
  const list: Array<{ id: string | number; name: string }> = [];

  // Add "no-part" section first
  list.push({
    id: "no-part",
    name: t("dashboard:fieldObservations.part.none"),
  });

  // Add parts
  if (parts.value) {
    parts.value.forEach((p) => {
      list.push({ id: p.id, name: p.name });
    });
  }

  return list;
});

// Editor state
const showEditor = ref(false);
const selectedObservation = ref<FieldObservation | undefined>(undefined);
const editorInitialImages = ref<File[]>([]);
const editorInitialAudioBlob = ref<Blob | undefined>(undefined);

const openEditor = (observation?: FieldObservation) => {
  selectedObservation.value = observation;
  editorInitialImages.value = [];
  editorInitialAudioBlob.value = undefined;
  showEditor.value = true;
};

const openEditorWithImages = (files: File[]) => {
  selectedObservation.value = undefined;
  editorInitialImages.value = files;
  editorInitialAudioBlob.value = undefined;
  showEditor.value = true;
};

const openEditorWithAudio = (audioBlob: Blob) => {
  selectedObservation.value = undefined;
  editorInitialImages.value = [];
  editorInitialAudioBlob.value = audioBlob;
  showEditor.value = true;
};

const handleEditorClose = () => {
  showEditor.value = false;
  selectedObservation.value = undefined;
  editorInitialImages.value = [];
  editorInitialAudioBlob.value = undefined;
};

const handleSelectObservation = (observation: FieldObservation) => {
  openEditor(observation);
};

// Camera overlay
const showCameraOverlay = ref(false);
const photoInput = ref<HTMLInputElement | null>(null);

const openPhotoPicker = () => {
  if (isMobileOrTablet()) {
    showCameraOverlay.value = true;
  } else {
    photoInput.value?.click();
  }
};

const handleCameraClose = () => {
  showCameraOverlay.value = false;
};

const handleCameraCaptured = async (files: File[]) => {
  showCameraOverlay.value = false;
  openEditorWithImages(files);
};

const handlePhotoChange = async (event: Event) => {
  const input = event.target as HTMLInputElement;
  const files = input.files;
  if (files && files.length > 0) {
    openEditorWithImages(Array.from(files));
  }
  input.value = "";
};

// Voice recording overlay
const showVoiceOverlay = ref(false);

const openVoiceRecording = () => {
  showVoiceOverlay.value = true;
};

const handleVoiceClose = () => {
  showVoiceOverlay.value = false;
};

const handleVoiceRecorded = (audioBlob: Blob) => {
  showVoiceOverlay.value = false;
  openEditorWithAudio(audioBlob);
};

// FAB actions
const handleFabPhoto = () => {
  openPhotoPicker();
};

const handleFabAudio = () => {
  openVoiceRecording();
};

const handleFabNew = () => {
  openEditor();
};
</script>

<template>
  <div class="observations-list">
    <!-- AI Extracting Banner -->
    <div v-if="showAiExtractingBanner" class="ai-list-banner extracting">
      <div class="d-flex align-items-center gap-3">
        <i class="bi-stars" />
        <div class="flex-grow-1">
          <div>
            {{ t("dashboard:fieldObservations.partsTree.aiExtracting") }}
          </div>
          <div class="ai-banner-subtitle">
            {{ t("dashboard:fieldObservations.partsTree.aiExtractingHint") }}
          </div>
        </div>
      </div>
    </div>

    <!-- AI Suggestions Banner -->
    <div
      v-if="showAiSuggestionsBanner"
      class="ai-list-banner suggestions cursor-pointer"
      @click="openPartsFromBanner"
    >
      <div class="d-flex align-items-center gap-3">
        <i class="bi-stars" />
        <span class="flex-grow-1">
          {{ t("dashboard:fieldObservations.partsTree.aiSuggestionsTitle") }}
        </span>
        <button
          class="btn-close btn-close-white btn-sm"
          @click.stop="dismissAiBanner"
        />
      </div>
    </div>

    <!-- Filter bar -->
    <div class="filter-bar">
      <div class="filter-bar-content">
        <div class="sort-selector">
          <PhosphorIcon name="sort-ascending" :size="18" />

          <button
            v-for="key in ['date', 'type', 'part'] as SortKey[]"
            :key="key"
            class="sort-btn"
            :class="{ active: sortKey === key }"
            @click="sortKey = key"
          >
            {{ t(`dashboard:fieldObservations.sort.${key}`) }}
          </button>
        </div>
        <button class="parts-btn" @click="showPartsDrawer = true">
          <PhosphorIcon name="tree-structure" :size="16" />
        </button>
      </div>
    </div>

    <div class="observations-content">
      <!-- Title (only when not sorting by type) -->
      <div v-if="sortKey !== 'type'" class="list-header">
        <h2 class="list-title">
          {{ t("dashboard:fieldObservations.title") }}
          <span v-if="observations">({{ observations.length }})</span>
        </h2>
      </div>

      <!-- Loading state -->
      <div v-if="isLoading" class="loading-state">
        <span class="spinner-border" />
      </div>

      <!-- Empty state -->
      <div
        v-else-if="!observations || observations.length === 0"
        class="empty-state"
      >
        <PhosphorIcon name="clipboard-text" :size="48" class="empty-icon" />
        <p>{{ t("dashboard:fieldObservations.empty") }}</p>
      </div>

      <!-- Grouped by type -->
      <template v-else-if="sortKey === 'type'">
        <div
          v-for="type in ['DISORDER', 'OVERVIEW', 'COMMENT'] as const"
          :key="type"
          class="type-section"
        >
          <button class="type-section-header" @click="toggleTypeSection(type)">
            <span class="type-section-title">
              {{ t(typeConfigs[type].labelKey) }}
              <span class="type-count"
                >({{ observationsByType[type]?.length || 0 }})</span
              >
            </span>
            <PhosphorIcon
              name="caret-down"
              :size="18"
              class="chevron"
              :class="{ expanded: expandedTypes.has(type) }"
            />
          </button>
          <div
            v-if="
              expandedTypes.has(type) && observationsByType[type]?.length > 0
            "
            class="type-section-content"
          >
            <FieldObservationItem
              v-for="observation in observationsByType[type]"
              :key="observation.id"
              :observation="observation"
              @select="handleSelectObservation(observation)"
            />
          </div>
        </div>
      </template>

      <!-- Grouped by part -->
      <template v-else-if="sortKey === 'part'">
        <div
          v-for="partItem in partsToDisplay"
          :key="partItem.id"
          class="type-section"
        >
          <button
            class="type-section-header"
            @click="togglePartSection(partItem.id)"
          >
            <span class="type-section-title">
              {{ partItem.name }}
              <span class="type-count"
                >({{ observationsByPart[partItem.id]?.length || 0 }})</span
              >
            </span>
            <PhosphorIcon
              name="caret-down"
              :size="18"
              class="chevron"
              :class="{ expanded: expandedParts.has(partItem.id) }"
            />
          </button>
          <div
            v-if="
              expandedParts.has(partItem.id) &&
              observationsByPart[partItem.id]?.length > 0
            "
            class="type-section-content"
          >
            <FieldObservationItem
              v-for="observation in observationsByPart[partItem.id]"
              :key="observation.id"
              :observation="observation"
              @select="handleSelectObservation(observation)"
            />
          </div>
        </div>
      </template>

      <!-- Regular observations list -->
      <div v-else class="observations-grid">
        <FieldObservationItem
          v-for="observation in sortedObservations"
          :key="observation.id"
          :observation="observation"
          @select="handleSelectObservation(observation)"
        />
      </div>
    </div>

    <!-- Hidden photo input -->
    <input
      ref="photoInput"
      type="file"
      accept="image/*"
      multiple
      class="d-none"
      @change="handlePhotoChange"
    />

    <!-- FAB buttons -->
    <div class="fab-container">
      <button class="fab-btn fab-photo" @click="handleFabPhoto">
        <PhosphorIcon name="camera" :size="24" weight="fill" />
      </button>
      <button class="fab-btn fab-audio" @click="handleFabAudio">
        <PhosphorIcon name="microphone" :size="24" weight="fill" />
      </button>
      <button class="fab-btn fab-new" @click="handleFabNew">
        <PhosphorIcon name="plus" :size="28" weight="bold" />
      </button>
    </div>

    <!-- Editor -->
    <FieldObservationEditor
      v-if="showEditor"
      :diagnostic-id="diagnosticId"
      :observation="selectedObservation"
      :initial-images="editorInitialImages"
      :initial-audio-blob="editorInitialAudioBlob"
      @close="handleEditorClose"
    />

    <!-- Camera overlay -->
    <CameraOverlay
      v-if="showCameraOverlay"
      @close="handleCameraClose"
      @captured="handleCameraCaptured"
    />

    <!-- Voice recording overlay -->
    <VoiceRecordingOverlay
      v-if="showVoiceOverlay"
      :diagnostic-id="diagnosticId"
      @close="handleVoiceClose"
      @recorded="handleVoiceRecorded"
    />

    <!-- Parts tree editor -->
    <PartsTreeEditor
      v-if="showPartsDrawer"
      :diagnostic-id="diagnosticId"
      @close="showPartsDrawer = false"
    />
  </div>
</template>

<style scoped lang="scss">
@import "@/style/variables.scss";

.ai-list-banner {
  color: white;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 500;
  flex-shrink: 0;

  .bi-stars {
    font-size: 18px;
  }

  &.extracting {
    background: linear-gradient(
      135deg,
      color-mix(in srgb, $ia, black 10%) 0%,
      $ia 100%
    );

    .bi-stars {
      animation: pulse-stars 2s ease-in-out infinite;
    }
  }

  .ai-banner-subtitle {
    font-size: 12px;
    opacity: 0.75;
    font-weight: 400;
    margin-top: 2px;
  }

  &.suggestions {
    background: linear-gradient(135deg, #8b5cf6 0%, $ia 100%);
  }
}

@keyframes pulse-stars {
  0%,
  100% {
    opacity: 0.4;
    transform: scale(0.85);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
}

.cursor-pointer {
  cursor: pointer;
}

.observations-list {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.filter-bar {
  background-color: var(--bs-white);
  border-bottom: 1px solid var(--bs-border-color);
  padding: 6px 10px;
  flex-shrink: 0;
}

.filter-bar-content {
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.observations-content {
  max-width: 800px;
  width: 100%;
  margin: 0 auto;
  padding: 16px;
  padding-bottom: 100px;
  flex: 1;
  overflow-y: auto;
}

.list-header {
  margin-bottom: 1rem;
}

.list-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--bs-body-color);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.parts-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--bs-border-color);
  border-radius: var(--bs-border-radius);
  background-color: transparent;
  color: var(--bs-body-color);
  font-size: 13px;
  height: 32px;
  aspect-ratio: 1 / 1;
  padding: 0;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all 0.15s ease;

  i {
    font-size: 14px;
  }

  &:hover {
    border-color: var(--bs-primary);
    color: var(--bs-primary);
  }
}

.list-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--bs-body-color);
  margin: 0;
}

.sort-selector {
  display: flex;
  align-items: center;
  gap: 4px;
}

.sort-btn {
  border: 1px solid var(--bs-border-color);
  border-radius: var(--bs-border-radius);
  background-color: transparent;
  color: var(--bs-body-color);
  font-size: 13px;
  padding: 4px 6px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--bs-primary);
    color: var(--bs-primary);
  }

  &.active {
    border-color: var(--bs-primary);
    background-color: var(--bs-primary);
    color: white;
  }
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: 3rem;

  .spinner-border {
    width: 40px;
    height: 40px;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: var(--bs-secondary-color);
  text-align: center;

  .empty-icon {
    font-size: 48px;
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  p {
    font-size: 16px;
    margin: 0;
  }
}

.observations-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

// Type sections (when sorting by type)
.type-section {
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
}

.type-section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 14px 0;
  border: none;
  background-color: transparent;
  color: var(--bs-body-color);
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    opacity: 0.7;
  }
}

.type-section-title {
  flex: 1;
  text-align: left;
  color: var(--bs-body-color);
}

.type-count {
  font-weight: 400;
  color: var(--bs-secondary-color);
}

.chevron {
  color: var(--bs-secondary-color);
  transition: transform 0.2s ease;

  &.expanded {
    transform: rotate(180deg);
  }
}

.type-section-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 0 12px 0;
}

// FAB styles
.fab-container {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 100;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.fab-btn {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: none;
  color: white;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  transition: all 0.15s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &.fab-photo {
    background-color: var(--bs-primary);
  }

  &.fab-audio {
    background-color: #ef4444; // red
  }

  &.fab-new {
    background-color: var(--bs-primary); // blue
    width: 56px;
    height: 56px;
    font-size: 24px;
  }
}
</style>
